import { TextField } from '@mui/material'
import {
  DatePicker as DatePickerMui,
  LocalizationProvider
} from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/en-nz'
import { useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { API_DATE_FORMAT, UI_DATE_FORMAT, formatDate } from 'utils/util'

interface DatePickerProps {
  name: string
  label: string
  inputFormat?: 'DD/MM/YYYY' | 'YYYY-MM-DD'
  outputFormat?: 'DD/MM/YYYY' | 'YYYY-MM-DD'
}

interface CustomFieldProps {
  value?: Dayjs | null
  onChange?: (value: Dayjs | null, context: unknown) => void
  // MUI DatePicker injects the calendar button here — forwarded to TextField
  InputProps?: Record<string, unknown>
  label?: string
  error?: boolean
  helperText?: React.ReactNode
  name?: string
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  required?: boolean
  fullWidth?: boolean
  inputRef?: React.Ref<HTMLInputElement>
}

function applyDateMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

const INVALID_DATE_MESSAGE = 'Please enter a valid date (DD/MM/YYYY)'

const CustomDateField = React.forwardRef<HTMLDivElement, CustomFieldProps>(
  (
    {
      value,
      onChange,
      InputProps,
      label,
      error,
      helperText,
      name,
      onBlur,
      required,
      fullWidth,
      inputRef
    },
    ref
  ) => {
    const [textValue, setTextValue] = useState(
      value?.isValid() ? value.format(UI_DATE_FORMAT) : ''
    )
    const [invalidDate, setInvalidDate] = useState(false)

    useEffect(() => {
      if (value?.isValid()) {
        setTextValue(value.format(UI_DATE_FORMAT))
        setInvalidDate(false)
      }
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = applyDateMask(e.target.value)
      setTextValue(formatted)
      const parsed = dayjs(formatted, UI_DATE_FORMAT, true)
      if (parsed.isValid()) {
        setInvalidDate(false)
        onChange?.(parsed, { validationError: null })
      } else if (!formatted) {
        setInvalidDate(false)
        onChange?.(null, { validationError: null })
      } else {
        // Show error immediately once input is complete but still invalid
        setInvalidDate(formatted.length === 10)
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (textValue && !dayjs(textValue, UI_DATE_FORMAT, true).isValid()) {
        setInvalidDate(true)
      }
      onBlur?.(e)
    }

    return (
      <TextField
        ref={ref}
        label={label}
        value={textValue}
        onChange={handleChange}
        onBlur={handleBlur}
        name={name}
        required={required}
        fullWidth={fullWidth}
        error={invalidDate || !!error}
        helperText={invalidDate ? INVALID_DATE_MESSAGE : helperText}
        placeholder='dd/mm/yyyy'
        inputRef={inputRef}
        slotProps={{ input: InputProps }}
      />
    )
  }
)
CustomDateField.displayName = 'CustomDateField'

function parseDateValue(dateValue: string | null | undefined): Dayjs | null {
  if (!dateValue) return null
  const fromUI = dayjs(dateValue, UI_DATE_FORMAT, true)
  if (fromUI.isValid()) return fromUI
  const fromAPI = dayjs(dateValue, API_DATE_FORMAT, true)
  return fromAPI.isValid() ? fromAPI : null
}

export default function DatePicker<T>({
  name,
  label,
  outputFormat
}: Readonly<DatePickerProps>): JSX.Element {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleBlur,
    setFieldTouched
  } = useFormikContext<T>()

  const valuesAny = values as Record<string, unknown>
  const dateValue = valuesAny[name] as string | null | undefined
  const parsedDate = parseDateValue(dateValue)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-nz'>
      <DatePickerMui
        value={parsedDate}
        slots={{
          // @ts-expect-error - custom field doesn't match MUI's internal slot type
          field: CustomDateField
        }}
        slotProps={{
          field: {
            label,
            name,
            onBlur: handleBlur,
            required: true,
            fullWidth: true,
            // @ts-expect-error - dynamic property access
            error: touched[name] && !!errors[name],
            // @ts-expect-error - dynamic property access
            helperText: touched[name] && errors[name]
          } as CustomFieldProps
        }}
        onChange={date => {
          const formattedDate = date
            ? formatDate({
                date: dayjs(date).format(UI_DATE_FORMAT),
                inputFormat: UI_DATE_FORMAT,
                outputFormat: outputFormat ?? API_DATE_FORMAT
              })
            : ''
          setFieldTouched(name, true)
          setFieldValue(name, formattedDate)
        }}
      />
    </LocalizationProvider>
  )
}
